import { merchantsHaveDynamicLevels } from 'components/ReusableComponents/Arrivals/LoyaltyIcon/loyaltyLevels'
import LoadingIndicator from 'components/ReusableComponents/LoadingIndicator'
import Toggle from 'components/ReusableComponents/Toggle'
import { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import hotelApi from 'services/api/hotel'

import { TogglePill } from '.'
import styles from './FilterSidebar.module.scss'
import {
  checkLoyaltyProgram,
  getLoyaltyProgramLevelName,
  getloyaltyProgramLevelsList,
  getLoyaltyProgramLevelTitle
} from './helpers'

const LoyaltyProgramSections = ({ membership, setMembership, parentChain }) => {
  const groupName = useSelector(s => s.hotelDetails.groupName)

  const [parentChainCodes, setParentChainCodes] = useState([])
  const [isLoadingLoyalties, setIsLoadingLoyalties] = useState(false)
  const hasDynamicLevels = merchantsHaveDynamicLevels.includes(groupName)
  const hasLoyaltyProgram = checkLoyaltyProgram(groupName, parentChain)
  const programLevels = getloyaltyProgramLevelsList(parentChain)

  useEffect(() => {
    setIsLoadingLoyalties(true)
    hotelApi
      .getHotelLoyalPrograms()
      .then(data => {
        setParentChainCodes(data.parentChainCodes)
      })
      .catch(() => {
        setParentChainCodes([])
      })
      .finally(() => {
        setIsLoadingLoyalties(false)
      })
  }, [])

  const handleMembershipChange = (checked, level) => {
    if (checked) {
      const selectedMembershipData = {
        ...membership,
        loyalties: membership?.loyalties ? [...membership.loyalties, level] : [level]
      }
      setMembership(selectedMembershipData)
    } else {
      let updatedLoyalties = membership.loyalties.filter(
        l => !(l.level === level.level && l.program === level.program)
      )

      if (!level.isAnyLevel) {
        updatedLoyalties = updatedLoyalties.filter(
          l => !(l.isAnyLevel && l.program === level.program)
        )
      }

      const updatedMembership = {
        ...membership,
        loyalties: updatedLoyalties
      }
      setMembership(updatedMembership)
    }
  }

  const toggleUseLoyaltyFilters = e => {
    const checked = e.target.checked
    if (!checked) {
      const updatedMembership = {
        ...membership,
        loyalties: [],
        useLoyaltyFilters: false
      }
      setMembership(updatedMembership)
    } else {
      setMembership({
        ...membership,
        useLoyaltyFilters: true
      })
    }
  }

  const handleAnyLevelChange = checked => {
    if (checked) {
      const allLevels = programLevels.map(level => ({
        level: level.level,
        program: level.program,
        isNoLevel: false,
        isAnyLevel: false
      }))

      allLevels.push({
        level: null,
        program: null,
        isNoLevel: false,
        isAnyLevel: true
      })

      const updatedMembership = {
        ...membership,
        loyalties: allLevels
      }
      setMembership(updatedMembership)
    } else {
      const updatedMembership = {
        ...membership,
        loyalties: membership.loyalties.filter(l => !l.isAnyLevel)
      }
      setMembership(updatedMembership)
    }
  }

  const handleDynamicAnyLevelChange = (checked, program) => {
    if (checked) {
      const allLevels = program.levels.map(level => ({
        level: level.loyaltyLevel,
        program: program.loyaltyProgram,
        isNoLevel: false,
        isAnyLevel: false
      }))

      allLevels.push({
        level: null,
        program: program.loyaltyProgram,
        isNoLevel: false,
        isAnyLevel: true
      })

      const updatedMembership = {
        ...membership,
        loyalties: membership?.loyalties
          ? [
              ...membership.loyalties.filter(l => l.program !== program.loyaltyProgram),
              ...allLevels
            ]
          : [...allLevels]
      }
      setMembership(updatedMembership)
    } else {
      const updatedLoyalties = membership.loyalties.filter(
        l => l.program !== program.loyaltyProgram || !l.isAnyLevel
      )
      const updatedMembership = {
        ...membership,
        loyalties: updatedLoyalties
      }
      setMembership(updatedMembership)
    }
  }

  return (
    <div className={styles.controlGroup}>
      <div className={styles.loyaltyToggleContainer}>
        <div>
          <p className={styles.filterTitle}>Loyalty programs</p>
          <p className={styles.filterDescription}>
            {membership?.useLoyaltyFilters
              ? 'Specify loyalty levels to include'
              : 'All loyalty levels are included'}
          </p>
        </div>
        <Toggle
          className={styles.checkoutDateToggle}
          label="Use loyalty filters"
          checked={membership?.useLoyaltyFilters}
          handleChange={toggleUseLoyaltyFilters}
        />
      </div>

      {membership?.useLoyaltyFilters && (
        <div className={styles.loyatiesContainer}>
          {hasLoyaltyProgram && !hasDynamicLevels && (
            <div>
              <div>
                <p className={styles.filterTitle}>
                  {getLoyaltyProgramLevelTitle(parentChain)} level
                </p>
              </div>
              <div className={styles.pillHolder}>
                <TogglePill
                  onChange={handleAnyLevelChange}
                  checked={membership.loyalties.some(m => m.isAnyLevel === true)}
                >
                  Any level
                </TogglePill>
                {programLevels.map(level => (
                  <TogglePill
                    key={level.level}
                    onChange={checked => handleMembershipChange(checked, level)}
                    checked={membership.loyalties.some(l => l.level === level.level)}
                  >
                    {getLoyaltyProgramLevelName(parentChain, level.level)}
                  </TogglePill>
                ))}
              </div>
            </div>
          )}
          {hasLoyaltyProgram && hasDynamicLevels && (
            <div>
              {isLoadingLoyalties ? (
                <LoadingIndicator inline={true} />
              ) : (
                parentChainCodes.length > 0 &&
                parentChainCodes.map(parentChain => (
                  <div key={parentChain.parentChainCode}>
                    {parentChain?.programCounts?.length > 0 && (
                      <div>
                        {parentChain.programCounts.map(program => (
                          <div
                            key={program.loyaltyProgram}
                            className={styles.loyaltyProgram}
                          >
                            {program.levels.length > 0 && (
                              <div className={styles.programHolder}>
                                <p className={styles.programTitle}>
                                  {program.loyaltyProgram}
                                </p>
                                <div className={styles.levelPillHolder}>
                                  <TogglePill
                                    onChange={checked =>
                                      handleDynamicAnyLevelChange(checked, program)
                                    }
                                    checked={membership?.loyalties?.some(
                                      m =>
                                        m.isAnyLevel === true &&
                                        m.program === program.loyaltyProgram
                                    )}
                                  >
                                    Any level
                                  </TogglePill>
                                  {program.levels
                                    .slice()
                                    .sort((a, b) => {
                                      if (a.loyaltyLevel === 'No Level') return 1
                                      if (b.loyaltyLevel === 'No Level') return -1
                                      return 0
                                    })
                                    .map(level => (
                                      <TogglePill
                                        key={level.loyaltyLevel}
                                        onChange={checked =>
                                          handleMembershipChange(checked, {
                                            level: level.loyaltyLevel,
                                            program: program.loyaltyProgram
                                          })
                                        }
                                        checked={membership?.loyalties?.some(
                                          m =>
                                            m.level === level.loyaltyLevel &&
                                            m.program === program.loyaltyProgram
                                        )}
                                      >
                                        {level.loyaltyLevel}
                                      </TogglePill>
                                    ))}
                                </div>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))
              )}
            </div>
          )}
        </div>
      )}
    </div>
  )
}

export default LoyaltyProgramSections
